/**
 * Carriage Product Configurator JavaScript
 * Image-based configurator for garage/carriage products (no 3D model)
 * Handles variant selection, step transitions, and image switching based on configuration
 */

const DEBUG = false;
const log = (...args) => DEBUG && console.log('[CarriageModal]', ...args);

// ============================================================================
// UTILITIES
// ============================================================================

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => ctx.querySelectorAll(sel);

const q = (dataAttr, classSelector, ctx = document) => 
  $(dataAttr, ctx) || $(classSelector, ctx);

const qa = (dataAttr, classSelector, ctx = document) => {
  const result = $$(dataAttr, ctx);
  return result.length > 0 ? result : $$(classSelector, ctx);
};

const escapeHtml = (value) => {
  const s = String(value ?? '');
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
};

// DRY: shared in `assets/configurator-shared.js`
const normalizeNumber = (...args) => window.ConfiguratorShared.normalizeNumber(...args);

const moneyFromCents = (cents) => normalizeNumber(cents, 0) / 100;

const formatPrice = (dollars) => window.ConfiguratorShared.formatPriceSmart(dollars);

const formatOptionValue = (value) => value ? value.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';

// ============================================================================
// CARRIAGE CONFIGURATOR CLASS
// ============================================================================

class CarriageConfigurator {
  constructor() {
    this.step1Section = q('[data-configurator-step="1"]', '.carriage-step-1');
    this.step2Section = q('[data-configurator-step="2"]', '[data-carriage-step-2]');
    
    if (!this.step1Section) return;

    this.variantCards = qa('[data-variant-card]', '.carriage-variant-card', this.step1Section);
    this.priceDisplay = $('[data-product-price]', this.step1Section);
    this.comparePriceDisplay = $('[data-compare-price]', this.step1Section);
    this.thumbnails = qa('[data-product-thumbnail]', '.carriage-thumbnail', this.step1Section);
    this.mainImage = q('[data-product-main-image]', '.carriage-featured-image img', this.step1Section);
    this.radioButtons = $$('input[type="radio"][name="variant-selection"]', this.step1Section);
    this.designOwnBtn = $('[data-design-your-own]', this.step1Section);

    this.currentConfig = {};
    this.basePrice = 0;
    this.configPrice = 0;
    this.steps = [];
    this.currentStep = 1;
    this.selectedVariant = null;
    this.currentImages = {};
    this.userFormData = null;
    this.permitPlanSelected = 'exclude';
    this.permitPlanBasePrice = 6995;
    this.permitPlanPrice = 0;
    this.foundationTypeSelected = 'wood-frame';
    this.permitDrawer = null;
    this.foundationDrawer = null;
    this.errorBanner = null;
    this.finalPriceData = null;
    this.finalPriceFetchTimer = null;
    this.finalPriceAbort = null;
    this.finalPriceLastPayloadKey = null;

    if (this.variantCards.length === 0) return;
    this.init();
  }

  // ============================================================================
  // USER-FACING STATUS / ERRORS
  // ============================================================================

  ensureErrorBanner() {
    if (this.errorBanner) return this.errorBanner;

    const container =
      this.step1Section?.querySelector('.carriage-cta-section') ||
      this.step1Section?.querySelector('.carriage-product-card') ||
      this.step1Section;

    if (!container) return null;

    const el = document.createElement('div');
    // Reuse theme alert styles instead of custom CSS
    el.className = 'alert-danger-bg alert-danger mt-2 rounded';
    el.setAttribute('role', 'alert');
    el.style.display = 'none';
    container.appendChild(el);
    this.errorBanner = el;
    return el;
  }

  showError(message) {
    const banner = this.ensureErrorBanner();
    if (banner) {
      banner.textContent = message;
      banner.style.display = '';
    }
  }

  clearError() {
    const banner = this.ensureErrorBanner();
    if (banner) {
      banner.textContent = '';
      banner.style.display = 'none';
    }
  }

  init() {
    log('CarriageConfigurator initializing...');
    
    this.setupVariantSelection();
    this.setupRadioChangeEvents();
    this.setupThumbnailSwitching();
    this.setupDesignOwnButton();
    
    if (this.step2Section) this.setupStep2();
    
    log('CarriageConfigurator initialized');
  }

  // ============================================================================
  // DRAWER HELPERS (shared)
  // ============================================================================

  getDrawer(id) {
    return document.getElementById(id);
  }

  openDrawerById(id, drawerRefProp) {
    if (!drawerRefProp) return;
    if (!this[drawerRefProp]) this[drawerRefProp] = this.getDrawer(id);
    const drawer = this[drawerRefProp];
    if (!drawer) return;

    if (typeof drawer.openDrawer === 'function') {
      drawer.openDrawer();
      return;
    }

    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    if (typeof SiteOverlay !== 'undefined' && SiteOverlay.showOverlay) SiteOverlay.showOverlay();
  }

  updateDrawerSelection(drawer, selector, getValue, selectedValue) {
    if (!drawer) return;
    drawer.querySelectorAll(selector).forEach(card => {
      const isSelected = getValue(card) === selectedValue;
      card.classList.toggle('selected', isSelected);
    });
  }

  setSingleSelected(drawer, selector, selectedCard) {
    if (!drawer) return;
    drawer.querySelectorAll(selector).forEach(c => c.classList.remove('selected'));
    selectedCard.classList.add('selected');
  }

  // ============================================================================
  // TAB HELPERS
  // ============================================================================

  getAvailableTabs() {
    const tabs = [];

    const hasOptions =
      this.configOptions?.garageAccess &&
      Array.isArray(this.configOptions.garageAccess) &&
      this.configOptions.garageAccess.length > 0;

    if (hasOptions) tabs.push({ name: 'options', label: 'Options' });

    // Match other models: always include Services + Summary
    tabs.push({ name: 'services', label: 'Services' });
    tabs.push({ name: 'summary', label: 'Summary' });

    return tabs;
  }

  getActiveTabName() {
    return q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab || null;
  }

  updateNavButtons() {
    const tabs = this.getAvailableTabs();
    const currentTab = this.getActiveTabName() || tabs[0]?.name;
    const idx = Math.max(0, tabs.findIndex(t => t.name === currentTab));

    const backBtn = $('[data-back-step]', this.step2Section);
    if (backBtn) backBtn.style.display = idx === 0 ? 'none' : '';

    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    const next = tabs[idx + 1];
    if (nextBtnText) nextBtnText.textContent = next ? next.label : 'Next';

    // Update Summary tab enabled/disabled state based on form completion
    this.updateSummaryTabState();
  }

  // ============================================================================
  // STEP 1: VARIANT SELECTION
  // ============================================================================

  setupVariantSelection() {
    this.variantCards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantSelection(card);
      });
    });
  }

  setupRadioChangeEvents() {
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const card = e.target.closest('[data-variant-card], .carriage-variant-card');
          if (card) this.handleVariantSelection(card);
        }
      });
    });
  }

  handleVariantSelection(card) {
    this.variantCards.forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const radio = card.querySelector('input[type="radio"]');
    if (radio && !radio.checked) radio.checked = true;

    const { variantId, variantPriceFormatted, variantPrice, variantCompareAtPrice, variantImageId, variantImageSrc, variantName } = card.dataset;
    // Shopify variant.price is in cents; internal pricing uses dollars
    this.basePrice = moneyFromCents(variantPrice);
    this.selectedVariant = { id: variantId, imageId: variantImageId, imageSrc: variantImageSrc, name: variantName };

    // Map variant name to roofStyle in kebab-case for Bliss API
    if (variantName) {
      this.currentConfig.roofStyle = variantName.trim().toLowerCase().replace(/\s+/g, '-');
    }

    if (this.priceDisplay && variantPriceFormatted) {
      this.priceDisplay.textContent = variantPriceFormatted;
    }

    if (this.comparePriceDisplay) {
      if (variantCompareAtPrice) {
        this.comparePriceDisplay.textContent = variantCompareAtPrice;
        this.comparePriceDisplay.style.display = '';
      } else {
        this.comparePriceDisplay.style.display = 'none';
      }
    }

    if (variantImageId && variantImageSrc) {
      this.updateSliderForVariant(variantImageId, variantImageSrc);
    }

    if (variantId) {
      this.updateURL(variantId);
      this.dispatchVariantChange(variantId, this.basePrice);
    }
  }

  updateSliderForVariant(variantImageId, variantImageSrc) {
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    if (!mainSwiperEl || !mainSwiperEl.swiper) return;

    const mainSwiper = mainSwiperEl.swiper;
    let targetIndex = -1;
    
    mainSwiper.slides.forEach((slide, index) => {
      const img = slide.querySelector('[data-product-main-image]');
      if (img && img.dataset.mediaId === variantImageId) {
        targetIndex = index;
      }
    });

    if (targetIndex >= 0) {
      mainSwiper.slideTo(targetIndex);
      
      const thumbnailsSwiper = this.step1Section?.querySelector('[data-product-thumbnails-swiper]')?.swiper;
      if (thumbnailsSwiper) {
        this.thumbnails.forEach(t => t.classList.remove('active'));
        if (this.thumbnails[targetIndex]) {
          this.thumbnails[targetIndex].classList.add('active');
        }
        thumbnailsSwiper.slideTo(targetIndex);
      }
    }
  }

  setupDesignOwnButton() {
    if (this.designOwnBtn) {
      this.designOwnBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideHeader();
        this.showStep2();
      });
    }
  }

  hideHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-out, transform 0.5s ease-out, max-height 0.5s ease-out', 'important');
      topbar.style.setProperty('opacity', '0', 'important');
      topbar.style.setProperty('transform', 'translateY(-100%)', 'important');
      topbar.style.setProperty('max-height', '0', 'important');
      topbar.style.setProperty('overflow', 'hidden', 'important');
      // Hide completely after animation
      setTimeout(() => {
        topbar.style.setProperty('display', 'none', 'important');
      }, 500);
    }
  }

  showHeader() {
    const topbar = document.querySelector('[data-top-bar], .topbar');
    if (topbar) {
      // Show element first
      topbar.style.setProperty('display', '', 'important');
      // Get original height for smooth transition
      const originalHeight = topbar.scrollHeight;
      topbar.style.setProperty('max-height', originalHeight + 'px', 'important');
      // Add transition for smooth animation with longer delay
      topbar.style.setProperty('transition', 'opacity 0.5s ease-in, transform 0.5s ease-in, max-height 0.5s ease-in', 'important');
      // Trigger reflow to ensure initial state is applied
      void topbar.offsetHeight;
      // Animate in
      requestAnimationFrame(() => {
        topbar.style.setProperty('opacity', '1', 'important');
        topbar.style.setProperty('transform', 'translateY(0)', 'important');
        topbar.style.setProperty('overflow', '', 'important');
      });
    }
  }

  setupThumbnailSwitching() {
    const mainSwiperEl = this.step1Section?.querySelector('[data-product-main-swiper]');
    const thumbnailsSwiperEl = this.step1Section?.querySelector('[data-product-thumbnails-swiper]');
    
    if (!mainSwiperEl || !thumbnailsSwiperEl) return;

    const initSwipers = () => {
      if (mainSwiperEl.swiper && thumbnailsSwiperEl.swiper) {
        const mainSwiper = mainSwiperEl.swiper;
        const thumbnailsSwiper = thumbnailsSwiperEl.swiper;

        this.thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', () => {
            this.thumbnails.forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            mainSwiper.slideTo(slideIndex);
            thumbnailsSwiper.slideTo(index);
          });
        });

        mainSwiper.on('slideChange', () => {
          const activeIndex = mainSwiper.activeIndex;
          this.thumbnails.forEach((thumb, index) => {
            const slideIndex = parseInt(thumb.dataset.slideIndex || index, 10);
            if (slideIndex === activeIndex) {
              this.thumbnails.forEach(t => t.classList.remove('active'));
              thumb.classList.add('active');
              thumbnailsSwiper.slideTo(index);
            }
          });
        });
      } else {
        setTimeout(initSwipers, 100);
      }
    };

    initSwipers();
  }

  // ============================================================================
  // STEP 2: IMAGE-BASED CONFIGURATOR
  // ============================================================================

  async showStep2() {
    const selectedVariantId = this.getSelectedVariantId();
    if (!selectedVariantId) return;

    // Capture roofStyle (variant title) from selected variant card BEFORE showing step 2
    const selectedCard = this.step1Section?.querySelector('[data-variant-card].selected, .carriage-variant-card.selected');
    const roofStyleFromVariant = selectedCard?.dataset?.variantRoofStyle;
    
    if (roofStyleFromVariant) {
      this.currentConfig.roofStyle = roofStyleFromVariant;
      log('Captured roofStyle (variant title) from variant card:', roofStyleFromVariant);
    }

    this.clearError();
    this.showLoadingState();

    try {
      const sectionHTML = await this.fetchStep2Section(selectedVariantId);
      if (sectionHTML) {
        this.updateStep2Content(sectionHTML);
        // updateStep2Content may fail to find a valid section if HTML is unexpected
        if (!this.step2Section || !this.step2Section.innerHTML.trim()) {
          throw new Error('Configurator section HTML was empty');
        }

        document.body.classList.add('carriage-configurator-active');
        this.toggleStep2HiddenSections(false); // Hide extra sections in step 2
        this.setupStep2();
        this.loadStep2Data();
        this.hideLoadingState();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        ConfiguratorShared.StepHistoryManager.pushStep2(this);
      } else {
        this.hideLoadingState();
        this.showError('We could not load the configurator. Please try again.');
      }
    } catch (error) {
      console.error('Error loading Step 2:', error);
      this.hideLoadingState();
      const isAbort = error?.name === 'AbortError';
      this.showError(isAbort
        ? 'Loading timed out. Please check your connection and try again.'
        : 'We could not load the configurator. Please refresh the page and try again.');
    }
  }

  /**
   * Toggle visibility of sections that should be hidden in step 2
   * @param {boolean} show - true to show sections, false to hide
   */
  toggleStep2HiddenSections(show) {
    const footerForm = document.querySelector('div.newsletter-wrapper');
    
    if (footerForm) {
      footerForm.style.display = show ? '' : 'none';
    }
    
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) return;

    // Find all Shopify sections
    const allSections = mainContent.querySelectorAll('[id^="shopify-section-"]');
    
    allSections.forEach(section => {
      const sectionId = section.id;
      
      // Only keep step 2 sections visible, hide everything else
      // Exclude FAQ section - it's controlled separately by toggleFaqSection
      const isStep2Section = sectionId.includes('carriage_step_2') || sectionId.includes('carriage-step-2');
      const isFaqSection = sectionId.includes('global_modal_faq') || sectionId.includes('global-modal-faq');
      
      if (!isStep2Section && !isFaqSection) {
        section.style.display = show ? '' : 'none';
      }
    });
  }

  async fetchStep2Section(variantId) {
    let sectionId = this.step2Section?.dataset?.sectionId;
    if (!sectionId) {
      const wrapper = this.step2Section?.closest('[id^="shopify-section-"]');
      sectionId = wrapper?.id?.replace('shopify-section-', '') || 'carriage_step_2';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller.signal
    }).finally(() => clearTimeout(timeoutId));

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.text();
  }

  updateStep2Content(html) {
    if (!html || !this.step2Section) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSection = doc.querySelector('[data-configurator-step="2"]') ||
                       doc.querySelector('[data-carriage-step-2]') ||
                       doc.querySelector('.carriage-step-2') ||
                       doc.querySelector('section');

    if (newSection) {
      this.step2Section.innerHTML = newSection.innerHTML;
      if (newSection.dataset.sectionId) {
        this.step2Section.dataset.sectionId = newSection.dataset.sectionId;
      }
      this.step2Section.style.display = 'block';
    } else {
      // Leave step2 hidden and surface an error to the user
      this.showError('We could not load the configurator content. Please try again.');
    }
  }

  showLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.dataset.originalText = this.designOwnBtn.textContent;
      this.designOwnBtn.textContent = 'Loading...';
      this.designOwnBtn.disabled = true;
    }
  }

  hideLoadingState() {
    if (this.designOwnBtn) {
      this.designOwnBtn.textContent = this.designOwnBtn.dataset.originalText || 'Customize Your Carriage';
      this.designOwnBtn.disabled = false;
    }
  }

  hideStep2({ fromPopstate = false } = {}) {
    if (!fromPopstate) {
      ConfiguratorShared.StepHistoryManager.exitStep2();
      return;
    }

    // Ensure drawers/overlay are closed when leaving the configurator
    try { if (this.permitDrawer?.isOpen) this.permitDrawer.closeDrawer(); } catch (_) {}
    try { if (this.foundationDrawer?.isOpen) this.foundationDrawer.closeDrawer(); } catch (_) {}

    document.body.classList.remove('carriage-configurator-active');
    this.toggleStep2HiddenSections(true); // Show extra sections when leaving step 2
    if (this.step2Section) this.step2Section.style.display = 'none';
    this.hideLoadingState();
    this.showHeader();
    this.toggleFaqSection(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleFaqSection(show) {
    const faqSectionWrapper = document.querySelector('[id^="shopify-section-"][id*="global_modal_faq"], [id^="shopify-section-"][id*="global-modal-faq"]');
    const faqSection = document.querySelector('.global-modal-faq-section');
    const faqFooter = document.querySelector('.modal-faq-footer');
    const siteFooter = document.querySelector('.site-footer');
    if (show) {
      if (faqSectionWrapper) faqSectionWrapper.style.setProperty('display', 'block', 'important');
      if (faqSection) faqSection.style.setProperty('display', 'block', 'important');
      if (faqFooter) faqFooter.style.setProperty('display', 'block', 'important');
      if (siteFooter) siteFooter.style.setProperty('display', 'none', 'important');
    } else {
      if (faqSectionWrapper) faqSectionWrapper.style.removeProperty('display');
      if (faqSection) faqSection.style.removeProperty('display');
      if (faqFooter) faqFooter.style.removeProperty('display');
      if (siteFooter) siteFooter.style.removeProperty('display');
    }
  }

  setupStep2() {
    if (!this.step2Section) return;

    if (this.step2ClickHandler) {
      this.step2Section.removeEventListener('click', this.step2ClickHandler);
    }

    this.step2ClickHandler = (e) => {
      // Click on configurator title to go back to step 1
      if (e.target.closest('[data-configurator-title]')) {
        e.preventDefault();
        this.hideStep2();
        return;
      }

      const tab = e.target.closest('[data-tab-btn], .tab-btn');
      if (tab?.dataset.tab) {
        // Prevent navigation to disabled tabs (e.g., Summary when form not filled)
        if (tab.disabled || tab.classList.contains('disabled')) {
          e.preventDefault();
          return;
        }
        this.switchTab(tab.dataset.tab);
        return;
      }

      if (e.target.closest('[data-back-step]')) {
        e.preventDefault();
        this.goToPreviousTab();
        return;
      }

      if (e.target.closest('[data-next-step]')) {
        e.preventDefault();
        this.goToNextTab();
        return;
      }

      // Handle Save Design button click
      const saveDesignBtn = e.target.closest('[data-save-design]');
      if (saveDesignBtn) {
        e.preventDefault();
        
        // Handle Start Over button click
        if (saveDesignBtn?.dataset.startOver === 'true') {
          this.handleStartOver();
          return;
        }
        
        // Get current tab
        const currentTab = q('[data-tab-btn].active', '.tab-btn.active', this.step2Section)?.dataset.tab;
        
        // If already on services tab, try to submit the form
        if (currentTab === 'services') {
          if (!this.hasValidUserFormData()) {
            // Try to submit the form programmatically
            const form = $('[data-summary-form]', this.step2Section);
            if (form) {
              if (form.checkValidity()) {
                // Form is valid - submit it
                form.requestSubmit();
              } else {
                // Form is invalid - show validation messages and focus first invalid field
                form.reportValidity();
                this.focusSaveDesignForm();
              }
            } else {
              this.focusSaveDesignForm();
            }
          } else {
            // Form already submitted, go to summary
            this.switchTab('summary');
          }
          return;
        }
        
        // If not on services tab, redirect to services tab
        this.switchTab('services');
        
        // Scroll to form or installation options after a short delay to ensure content is rendered
        setTimeout(() => {
          const form = $('[data-summary-form]', this.step2Section);
          if (form && form.offsetParent !== null) {
            // Form is visible, focus on it
            this.focusSaveDesignForm();
          } else {
            // Form not visible yet (user needs to select installation first), focus on installation options
            this.focusInstallationOptions();
          }
        }, 150);
        
        return;
      }
    };

    this.step2Section.addEventListener('click', this.step2ClickHandler);
  }

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  loadStep2Data() {
    const modelConfigEl = $('[data-model-config]', this.step2Section);
    const variantFallbackEl = $('[data-variant-fallback]', this.step2Section);
    const sectionSettingsEl = $('[data-section-settings]', this.step2Section);

    let modelConfig = null, variantFallback = null, sectionSettings = null;

    try {
      if (modelConfigEl?.textContent.trim()) {
        const rawData = modelConfigEl.textContent.trim();
        modelConfig = rawData.startsWith('"') && rawData.endsWith('"')
          ? JSON.parse(JSON.parse(rawData))
          : JSON.parse(rawData);
      }
    } catch (e) { console.error('Error parsing model config:', e); }

    try {
      if (variantFallbackEl?.textContent.trim()) {
        variantFallback = JSON.parse(variantFallbackEl.textContent);
      }
    } catch (e) { console.error('Error parsing variant fallback:', e); }

    try {
      if (sectionSettingsEl?.textContent.trim()) {
        sectionSettings = JSON.parse(sectionSettingsEl.textContent);
      }
    } catch (e) { console.error('Error parsing section settings:', e); }

    this.modelConfig = {
      ...(modelConfig || {}),
      ...(sectionSettings || {}),
      services: {
        ...(modelConfig?.services || {}),
        ...(sectionSettings?.services || {})
      }
    };

    // Store variant documents for downloads section
    this.variantDocuments = variantFallback?.documents || [];

    // Extract configuration options (like garageAccess)
    this.configOptions = modelConfig || {};
    // Prefer Shopify variant price (cents) over JSON basePrice (dollars). JSON option prices are already dollars.
    if (variantFallback && variantFallback.price != null) {
      this.basePrice = moneyFromCents(variantFallback.price);
    } else if (typeof modelConfig?.basePrice === 'number') {
      // model_config_json basePrice is expected to be dollars
      this.basePrice = modelConfig.basePrice;
    } else {
      this.basePrice = 0;
    }

    // Permit plans / foundation defaults (match other configurators)
    this.permitPlanBasePrice = this.modelConfig?.permitPlanPrice ?? this.permitPlanBasePrice ?? 6995;
    // Permit plan price is set to 0 by default (excluded)
    // User can choose to include it via the permit plans drawer
    if (!this.foundationTypeSelected) this.foundationTypeSelected = 'wood-frame';

    // Set roofStyle from modelConfig if not already set from variant card
    if (!this.currentConfig.roofStyle) {
      const configRoofStyle = this.modelConfig?.roofStyle || this.modelConfig?.roof || this.modelConfig?.roofType;
      if (configRoofStyle) {
        this.currentConfig.roofStyle = configRoofStyle;
        log('Set roofStyle from modelConfig:', configRoofStyle);
      }
    }

    this.updateStep2Header();
    this.initializeDefaultServices();
    this.setupPermitPlansDrawer();
    this.setupFoundationTypeDrawer();
    this.generateTabs();
    const defaultTab = this.getAvailableTabs()[0]?.name || 'services';
    this.switchTab(defaultTab);
  }

  initializeDefaultServices() {
    const installationOptions = this.modelConfig?.services?.installation?.options || [];
    if (!installationOptions.length) return;

    if (!this.currentConfig.installation) {
      this.currentConfig.installation =
        installationOptions.find(o => o.isDefault)?.value ||
        installationOptions[0]?.value;
    }
  }

  // ============================================================================
  // TABS & NAVIGATION
  // ============================================================================

  generateTabs() {
    const tabsContainer = $('[data-configurator-tabs]', this.step2Section);
    if (!tabsContainer) return;

    tabsContainer.innerHTML = '';

    const tabs = this.getAvailableTabs();
    tabs.forEach((tab, index) => {
      tabsContainer.appendChild(this.createTabButton(tab.label, index + 1, index === 0));
    });
  }

  createTabButton(name, step, isActive) {
    const btn = document.createElement('button');
    btn.className = `tab-btn${isActive ? ' active' : ''}`;
    btn.setAttribute('data-tab-btn', '');
    btn.dataset.tab = name.toLowerCase();
    btn.dataset.step = step;
    btn.textContent = name;
    return btn;
  }

  /**
   * Enable or disable the Summary tab based on form completion state.
   * Disabled tabs cannot be clicked and show visual feedback.
   */
  updateSummaryTabState() {
    const summaryTab = q('[data-tab-btn][data-tab="summary"]', '.tab-btn[data-tab="summary"]', this.step2Section);
    if (!summaryTab) return;

    const isEnabled = this.hasValidUserFormData();
    summaryTab.disabled = !isEnabled;
    summaryTab.classList.toggle('disabled', !isEnabled);
    summaryTab.setAttribute('aria-disabled', String(!isEnabled));
    
    if (!isEnabled) {
      summaryTab.title = 'Complete the installation form to view summary';
    } else {
      summaryTab.removeAttribute('title');
    }
  }

  switchTab(tabName) {
    const tabLower = (tabName || '').toLowerCase();

    // Restore default 8-4 layout when NOT switching to summary (summary uses 6-6 layout)
    if (tabLower !== 'summary') {
      this.restoreDefaultLayout();
    }

    qa('[data-tab-btn]', '.tab-btn', this.step2Section).forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabLower);
    });

    // Show/hide FAQ section and footer based on current tab
    this.toggleFaqSection(tabLower === 'summary');

    const footer = $('[data-panel-footer]', this.step2Section);
    if (footer) footer.style.display = tabLower === 'summary' ? 'none' : '';

    // Reset START OVER state when leaving Summary
    if (tabLower !== 'summary') {
      const saveDesignBtn = $('[data-save-design]', this.step2Section);
      if (saveDesignBtn?.dataset.startOver === 'true') {
        saveDesignBtn.textContent = 'SAVE DESIGN';
        delete saveDesignBtn.dataset.startOver;
      }

      // Show header Save Design button when not on services form (will be hidden again in populateServicesOptions if needed)
      if (tabLower !== 'services' && saveDesignBtn) {
        saveDesignBtn.style.display = '';
      }
    }

    if (tabLower === 'summary') this.showSummary();
    else if (tabLower === 'services') this.populateServicesOptions();
    else this.populateOptions();

    // Pass skipQueueUpdate=true to prevent API calls on tab switch - API should only be called on Services step form submission
    if (tabLower !== 'summary') this.updateTotalPrice(true);
    this.updateNavButtons();

    // Scroll to top when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goToNextTab() {
    const tabs = this.getAvailableTabs();
    const currentTab = this.getActiveTabName() || tabs[0]?.name;
    const idx = tabs.findIndex(t => t.name === currentTab);
    const next = tabs[idx + 1];
    if (currentTab === 'services' && !this.hasValidUserFormData()) {
      // Try to submit the form programmatically when clicking SAVE DESIGN button
      const form = $('[data-summary-form]', this.step2Section);
      if (form) {
        if (form.checkValidity()) {
          // Form is valid - submit it
          form.requestSubmit();
        } else {
          // Form is invalid - show validation messages and focus first invalid field
          form.reportValidity();
          this.focusSaveDesignForm();
        }
      } else {
        this.focusSaveDesignForm();
      }
      return;
    }
    if (next) this.switchTab(next.name);
  }

  goToPreviousTab() {
    const tabs = this.getAvailableTabs();
    const currentTab = this.getActiveTabName() || tabs[0]?.name;
    const idx = tabs.findIndex(t => t.name === currentTab);
    const prev = tabs[idx - 1];
    if (prev) this.switchTab(prev.name);
  }

  // ============================================================================
  // OPTIONS RENDERING
  // ============================================================================

  populateOptions() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';

    // Render garage access options if available
    if (this.configOptions.garageAccess && Array.isArray(this.configOptions.garageAccess)) {
      const section = this.createConfigSection('garageAccess', 'Garage Access', this.configOptions.garageAccess);
      panelContent.appendChild(section);
    }

    contentWrapper.appendChild(panelContent);
    // Pass skipQueueUpdate=true to prevent API calls on tab switch
    this.updateTotalPrice(true);
    this.updateConfigImage();
  }

  createConfigSection(sectionId, title, options) {
    const section = document.createElement('div');
    section.className = 'config-section mb-6';
    section.dataset.configSection = sectionId;

    const titleEl = document.createElement('div');
    titleEl.className = 'config-title d-flex align-items-center gap-1 mb-2';
    titleEl.innerHTML = `<p class="config-title-label fw-medium text-black mb-0">${title}:</p> <span class="config-title-value fs-sm" style="letter-spacing: -0.01em;">${options[0]?.displayName || ''}</span>`;
    section.appendChild(titleEl);

    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'config-options';
    optionsContainer.dataset.configOptions = sectionId;

    options.forEach((option, index) => {
      const hasSavedValue = Boolean(this.currentConfig[sectionId]);
      const isSelected = hasSavedValue
        ? option.displayName === this.currentConfig[sectionId]
        : index === 0;
      if (isSelected && !this.currentConfig[sectionId]) {
        this.currentConfig[sectionId] = option.displayName;
        // Store images for this option
        if (option.images) {
          this.currentImages = option.images;
        }
      }
      optionsContainer.appendChild(this.createOptionCard(sectionId, option, isSelected));
    });

    section.appendChild(optionsContainer);
    return section;
  }

  createOptionCard(sectionId, option, isSelected) {
    const card = document.createElement('div');
    const isColor = option.hex || option.rgb;
    const isTexture = option.thumbnail || option.texture;

    let classes = 'option-card';
    if (isSelected) classes += ' selected';
    if (isColor) classes += ' option-card--color';
    if (isTexture) classes += ' option-card--texture';

    card.className = classes;
    card.dataset.optionCard = '';
    card.dataset.section = sectionId;
    card.dataset.value = option.displayName;
    card.dataset.price = option.price || 0;

    if (option.images) {
      card.dataset.images = JSON.stringify(option.images);
    }

    let html = `<span class="aspect-variant-check">
                  <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
                    <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"/>
                  </svg>
                </span>`;

    if (isColor) {
      html += `<div class="option-card-swatch" style="background-color: ${option.hex || option.rgb};"></div>`;
    } else if (isTexture) {
      html += `<div class="option-card-thumbnail"><img src="${option.texture || option.thumbnail}" alt="${option.displayName}" loading="lazy"></div>`;
    } else {
      const icon = option.iconImage || option.image
        ? `<img src="${option.iconImage || option.image}" alt="${option.displayName}" width="48" height="48" loading="lazy" class="w-100 h-100 object-fit-contain">`
        : '<svg viewBox="0 0 48 48" stroke-width="1.5" stroke="currentColor" fill="none"><rect x="8" y="8" width="32" height="32" rx="4"/><circle cx="24" cy="24" r="8"/></svg>';
      html += `<div class="option-card-icon">${icon}</div>`;
    }
    
    html += `<div class="option-card-content">`;
    html += `<p class="option-card-label fw-medium mb-0 text-black">${option.displayName}</p>`;
    html += option.price > 0
      ? `<p class="option-card-included text-gray-600 mb-0">+${formatPrice(option.price)}</p>`
      : '<p class="option-card-included text-gray-600 mb-0">Included</p>';
    html += `</div>`;
    
    card.innerHTML = html;
    card.addEventListener('click', () => this.selectOption(sectionId, card, option));
    return card;
  }

  selectOption(sectionId, card, option) {
    const container = card.parentElement;
    qa('[data-option-card]', '.option-card', container).forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    const titleEl = $(`[data-config-value="${sectionId}"]`, this.step2Section);
    if (titleEl) {
      titleEl.textContent = option.displayName;
    }

    this.currentConfig[sectionId] = option.displayName;
    
    // Update displayed image if option has images
    if (option.images) {
      this.currentImages = option.images;
      this.updateConfigImage();
    }

    // STATE RESET: If form was already submitted and user changes options,
    // invalidate the form data (requires re-submission before accessing Summary).
    // ⚠️ DO NOT call the installation API here - only clear form state.
    if (this.hasValidUserFormData()) {
      this.userFormData = null;
      window.studioShedUserData = null;
      this.finalPriceData = null;
      this._finalPriceDataZip = null; // Clear cached ZIP so API is called again on next form submit
      // Disable Summary tab immediately
      this.updateSummaryTabState();
    }

    // Update price display (skipQueueUpdate=true to prevent API call)
    this.updateTotalPrice(true);
  }

  /**
   * Update the displayed configuration image
   */
  updateConfigImage() {
    const primaryEl = $('[data-config-image]', this.step2Section);
    const secondaryEl = $('[data-config-image-secondary]', this.step2Section);
    if (!primaryEl || !this.currentImages) return;

    const preferredTop =
      this.currentImages.interiorStair ||
      this.currentImages.exterior ||
      this.currentImages.interior ||
      null;

    const preferredBottom =
      this.currentImages.interior ||
      this.currentImages.exterior ||
      null;

    const topUrl = preferredTop;
    const bottomUrl = preferredBottom && preferredBottom !== preferredTop ? preferredBottom : null;

    if (topUrl && primaryEl.src !== topUrl) {
      primaryEl.style.opacity = '0.5';
      primaryEl.src = topUrl;
      primaryEl.onload = () => { primaryEl.style.opacity = '1'; };
      log('Updated primary config image to:', topUrl);
    }

    if (secondaryEl) {
      if (bottomUrl) {
        secondaryEl.style.display = '';
        if (secondaryEl.src !== bottomUrl) {
          secondaryEl.style.opacity = '0.5';
          secondaryEl.src = bottomUrl;
          secondaryEl.onload = () => { secondaryEl.style.opacity = '1'; };
          log('Updated secondary config image to:', bottomUrl);
        }
      } else {
        secondaryEl.style.display = 'none';
      }
    }
  }

  // ============================================================================
  // PRICING
  // ============================================================================

  updateStep2Header() {
    const titleEl = $('[data-configurator-title]', this.step2Section);
    // Get ALL price elements (header and mobile footer)
    const priceEls = $$('[data-configurator-price]', this.step2Section);
    const specsEl = $('[data-configurator-specs]', this.step2Section);

    // Get base product name from h1
    const productName = this.step1Section?.querySelector('h1')?.textContent || '';
    
    // Get variant name from selected variant
    const variantName = this.selectedVariant?.name || '';
    
    // Combine product name + variant name (e.g., "Carriage House Gable")
    if (titleEl) {
      titleEl.textContent = variantName ? `${productName} ${variantName}` : productName;
    }
    
    // Update ALL price elements
    priceEls.forEach(el => el.textContent = formatPrice(this.basePrice));
    
    // Display size and square footage from JSON config
    if (specsEl) {
      const size = this.configOptions?.size || this.modelConfig?.size || '';
      const sqft = this.configOptions?.squareFootage || this.modelConfig?.squareFootage || '';
      
      if (size || sqft) {
        const parts = [];
        
        // Format size like "24x22" to "24' x 22'"
        if (size) {
          const formattedSize = size.replace(/x/gi, "' x ") + "'";
          parts.push(formattedSize);
        }
        
        // Add square footage
        if (sqft) {
          parts.push(`${sqft} sq.ft.`);
        }
        
        specsEl.textContent = parts.join(' | ');
      }
    }
  }

  updateTotalPrice(skipQueueUpdate = false) {
    // Prevent infinite loops by allowing callers to skip the queue update
    if (this._updatingPrice) return; // Guard against recursive calls
    this._updatingPrice = true;

    try {
      let optionsPrice = 0;

      // Calculate prices from configuration
      Object.keys(this.currentConfig).forEach(key => {
        const section = $(`[data-config-section="${key}"]`, this.step2Section);
        if (section) {
          const selectedCard = section.querySelector('.option-card.selected');
          if (selectedCard) {
            const price = normalizeNumber(selectedCard.dataset.price, 0);
            optionsPrice += price;
          }
        }
      });

      // Base product option price (excludes shipping/installation/permit)
      this.configPrice = optionsPrice;

      // Add installation + shipping costs from API (only after the user submits the form with ZIP)
      let servicesPrice = 0;
      if (this.hasValidUserFormData()) {
        const apiShipping = normalizeNumber(this.finalPriceData?.shipping_cost, 0);
        const apiInstallSelected = window.ConfiguratorShared.computeInstallationCost(
          this.finalPriceData?.breakdown?.installation,
          this.currentConfig?.installation
        );
        servicesPrice = apiShipping + apiInstallSelected;
      }
      const totalPrice = this.basePrice + optionsPrice + (this.permitPlanPrice || 0) + servicesPrice;

      // Update ALL price elements (header and mobile footer)
      const priceEls = $$('[data-configurator-price]', this.step2Section);
      priceEls.forEach(el => el.textContent = formatPrice(totalPrice));

      // Update ALL monthly price elements
      const monthlyEls = $$('[data-monthly-price]', this.step2Section);
      monthlyEls.forEach(el => el.textContent = formatPrice(Math.round(totalPrice / 180)));

      // Only queue update if explicitly requested and not already in progress
      // This prevents infinite loops - queueFinalPriceUpdate will call updateTotalPrice with skipQueueUpdate=true
      if (!skipQueueUpdate && this.hasValidUserFormData() && !this._priceUpdateQueued) {
        this._priceUpdateQueued = true;
        // Use requestAnimationFrame to defer the API call and prevent blocking
        requestAnimationFrame(() => {
          this._priceUpdateQueued = false;
          this.queueFinalPriceUpdate();
        });
      }
    } finally {
      this._updatingPrice = false;
    }
  }

  // ============================================================================
  // SERVICES
  // ============================================================================

  getModelSlug() {
    const raw =
      this.modelConfig?.model ||
      this.modelConfig?.name ||
      this.step1Section?.dataset?.model ||
      'carriage-house';
    return window.ConfiguratorShared.canonicalModel(raw);
  }

  buildFinalPricePayload(installationTypeOverride) {
    const zip = String(this.userFormData?.zipCode || '').trim();
    const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

    const depth = normalizeNumber(this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth ?? this.modelConfig?.model?.depth, null);
    const length = normalizeNumber(this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length ?? this.modelConfig?.model?.length, null);

    return window.ConfiguratorShared.buildInstallationCostPayload({
      zip,
      model: this.getModelSlug(),
      depth,
      length,
      interior: pick('interior', 'interiorFinish', 'mainFloorFinish'),
      kitchen: pick('kitchen') || '',
      roofStyle: pick('roofStyle', 'roof', 'roofType'),
    });
  }

  async fetchFinalPrice(payload) {
    // DRY: moved to `assets/configurator-shared.js` installation cost client.
    return payload;
  }

  queueFinalPriceUpdate(servicesContainer) {
    if (!this.hasValidUserFormData()) return;

    if (!this.installationCostClient) {
      this.installationCostClient = window.ConfiguratorShared.createInstallationCostClient();
    }

    const selectedType = String(this.currentConfig.installation || 'shell');
    const depth = normalizeNumber(this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth ?? this.modelConfig?.model?.depth, null);
    const length = normalizeNumber(this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length ?? this.modelConfig?.model?.length, null);

    window.ConfiguratorShared.queueInstallationPricing({
      client: this.installationCostClient,
      zip: this.userFormData?.zipCode,
      modelRaw: this.getModelSlug(),
      depth,
      length,
      currentConfig: this.currentConfig,
      selectedType
    })
      .then((out) => {
        if (!out?.installation) return;
        this.finalPriceData = out.finalPriceData;

        window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
          container: servicesContainer || this.step2Section,
          installation: out.installation,
          formatPrice,
          prefixPlus: true,
        });

        // Pass skipQueueUpdate=true to prevent infinite loop
        this.updateTotalPrice(true);
        this.recalculateSummaryTotal?.();

        // Notify only when we actually fetched new pricing (avoid spam on cached recalcs).
        if (out?.meta?.didFetch && out?.meta?.payloadKey) {
          if (this._lastInstallPricingToastKey !== out.meta.payloadKey) {
            this._lastInstallPricingToastKey = out.meta.payloadKey;
            window.ConfiguratorShared.showSuccess('Installation pricing updated.', 'Pricing');
          }
        }
      })
      .catch((e) => {
        const msg = e?.message || String(e || 'Installation pricing request failed');
        window.ConfiguratorShared.showError(msg, 'Installation pricing');
      });
  }

  applyFinalPriceToServicesUI(container) {
    if (!container || !this.finalPriceData) return;

    const install = this.finalPriceData?.breakdown?.installation;
    if (!install) return;

    window.ConfiguratorShared.applyInstallationCostsToServiceOptions({
      container,
      installation: install,
      formatPrice,
      prefixPlus: true,
    });
  }

  hasValidUserFormData() {
    return Boolean(
      this.userFormData &&
      this.userFormData.firstName &&
      this.userFormData.email &&
      this.userFormData.zipCode
    );
  }

  focusSaveDesignForm() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const firstInput =
      contentWrapper.querySelector('[data-summary-form] input[name="firstName"]') ||
      contentWrapper.querySelector('[data-summary-form] input') ||
      null;
    if (firstInput) {
      firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInput.focus({ preventScroll: true });
    }
  }

  focusInstallationOptions() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    const installSection =
      contentWrapper.querySelector('[data-config-section="installation"]') ||
      contentWrapper.querySelector('[data-services-options]') ||
      null;
    if (installSection) installSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getSaveDesignFormHTML() {
    return `
      <div class="config-section summary-form-view" data-save-design-form>
        <div class="text-center mb-md-5 mb-3 pe-xl-1">
          <h2 class="h3 mb-md-3 mb-2">You're almost done!</h2>
          <p class="mb-0">Your build is ready. Save your design by completing the form and get access to information about pricing, shipping, professional assembly, local permits, and financing options.</p>
        </div> 
        <form class="summary-contact-form" data-summary-form>
          <div class="input-group mb-2"><input type="text" name="firstName" placeholder="First Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="lastName" placeholder="Last Name *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="email" name="email" placeholder="Email *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="text" name="zipCode" placeholder="ZIP code *" class="form-control" required></div>
          <div class="input-group mb-2"><input type="tel" name="phone" placeholder="Phone Number" class="form-control"></div>
          <p class="form-disclaimer mb-xl-13 mb-md-7 mb-4">By providing a telephone number and when scheduling your consultation you are consenting to be contacted by SMS text message. Message & data rates may apply. You can reply STOP to opt-out of further messaging.</p>
          <button type="submit" class="visually-hidden" aria-hidden="true" tabindex="-1">Submit</button>
        </form>
      </div>
    `;
  }

  attachSaveDesignFormHandlers(container) {
    const form = $('[data-summary-form]', container);
    if (!form) return;

    // Create and attach form validator for real-time validation
    const validator = window.ConfiguratorShared?.createFormValidator?.(form);
    if (validator) {
      validator.attachListeners();
      this._formValidator = validator;
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // Validate form using our custom validator first
      if (this._formValidator) {
        const { valid, firstInvalid } = this._formValidator.validateAll();
        if (!valid) {
          firstInvalid?.focus();
          firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      
      // Prevent double submission
      if (this._isValidatingZip) return;
      
      const formData = new FormData(form);
      const pendingUserData = {
        firstName: String(formData.get('firstName') || ''),
        lastName: String(formData.get('lastName') || ''),
        email: String(formData.get('email') || ''),
        zipCode: String(formData.get('zipCode') || ''),
        phone: String(formData.get('phone') || '')
      };

      // Show loading state on submit button
      const submitBtn = form.querySelector('[data-next-step], button[type="submit"], .next-step-btn');
      const footerBtn = $('[data-next-step]', this.step2Section);
      const btnToDisable = submitBtn || footerBtn;
      const nextBtnTextSpan = $('[data-next-step-text]', this.step2Section);
      const originalBtnText = nextBtnTextSpan?.textContent || 'SAVE DESIGN';
      
      try {
        this._isValidatingZip = true;
        if (btnToDisable) {
          btnToDisable.disabled = true;
        }
        // Show "Loading..." text on button (similar to Design Your Own button)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = 'Loading...';
        }

        // Build payload for validation
        const depth = normalizeNumber(this.modelConfig?.depth ?? this.modelConfig?.model?.dimensions?.depth ?? this.modelConfig?.model?.depth, null);
        const length = normalizeNumber(this.modelConfig?.length ?? this.modelConfig?.model?.dimensions?.length ?? this.modelConfig?.model?.length, null);
        const pick = (...keys) => window.ConfiguratorShared.pickFirst(this.currentConfig, keys);

        // CRITICAL: Validate ZIP code with API BEFORE allowing step progression
        const result = await window.ConfiguratorShared.validateZipCodeWithAPI({
          zip: pendingUserData.zipCode,
          model: this.getModelSlug(),
          depth,
          length,
          interior: pick('interior', 'interiorFinish', 'mainFloorFinish'),
          kitchen: pick('kitchen') || '',
          roofStyle: pick('roofStyle', 'roof', 'roofType'),
          base_product_price: this.basePrice + this.configPrice,
        });

        // API returned 200 and valid data - NOW we can proceed
        this.userFormData = pendingUserData;
        window.studioShedUserData = this.userFormData;
        
        // Store the validated API data (new format from final-price API)
        if (result?.data) {
          const apiData = result.data;
          const breakdownInstallation = apiData.breakdown?.installation || {};
          const breakdownShipping = apiData.breakdown?.shipping || {};
          const selectedType = String(this.currentConfig.installation || 'shell');
          
          // Build installation object with prices from breakdown
          const installationWithPrices = {
            shell: normalizeNumber(breakdownInstallation.shell, 0),
            lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
            kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
            selected_type: breakdownInstallation.selected_type || selectedType,
            selected_cost: normalizeNumber(breakdownInstallation.selected_cost || apiData.installation_cost, 0),
            state_code: breakdownInstallation.state_code,
            zone: breakdownInstallation.zone,
          };
          
          this.finalPriceData = {
            breakdown: { 
              installation: installationWithPrices,
              installationPrices: {
                shell: normalizeNumber(breakdownInstallation.shell, 0),
                lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
                kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
              },
              shipping: {
                base_subtotal: normalizeNumber(breakdownShipping.base_subtotal, 0),
                depth_surcharge: normalizeNumber(breakdownShipping.depth_surcharge, 0),
                interior_surcharge: normalizeNumber(breakdownShipping.interior_surcharge, 0),
                inflation_surcharge: normalizeNumber(breakdownShipping.inflation_surcharge, 0),
              },
            },
            installation_cost: normalizeNumber(apiData.installation_cost, 0),
            shipping_cost: normalizeNumber(apiData.shipping_cost, 0),
            total_price: normalizeNumber(apiData.total_price, 0),
            product_price: normalizeNumber(apiData.product_price, 0),
            city: apiData.city || '',
            state_code: apiData.state_code || '',
          };
          // Track the ZIP code used to fetch this data to prevent duplicate API calls
          this._finalPriceDataZip = pendingUserData.zipCode;
        }

        // Call Save Design API (non-blocking - don't wait for it)
        // This runs in parallel with the UI update
        window.ConfiguratorShared.saveDesignToAPI(this, pendingUserData)
          .then((saveResult) => {
            if (saveResult?.success) {
              console.log('[Carriage] Design saved successfully:', saveResult.design_id);
            } else if (saveResult?.error) {
              console.warn('[Carriage] Save Design API:', saveResult.error);
            }
          })
          .catch((error) => {
            // Log error but don't block user flow
            console.error('[Carriage] Save Design API error:', error);
          });

        // SUCCESS: Show installation options
        this.populateServicesOptions(true);
        
        setTimeout(() => {
          this.focusInstallationOptions();
          // Update total price after switching tabs
          this.updateTotalPrice(true);
        }, 50);
        
      } catch (error) {
        // FAILURE: Stay on current step and show error
        const errorMessage = error?.message || 'ZIP code validation failed. Please try again.';
        window.ConfiguratorShared.showError(errorMessage, 'Invalid ZIP Code');
        
        // Show contact link below the Save Design button
        window.ConfiguratorShared.showContactLinkOnError(this.step2Section);
        
        // Focus the ZIP code input for correction
        const zipInput = form.querySelector('input[name="zipCode"]');
        if (zipInput) {
          zipInput.focus();
          zipInput.select();
        }
        
        // Do NOT switch tabs - stay on current step
        // Restore button text only on error (success case will have already switched to installation options with SUMMARY)
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        // Also re-enable the footer button if it's different from btnToDisable
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        // Also re-enable submit button if it's different
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
        // Restore the span text, not the entire button (to preserve button structure with arrow)
        if (nextBtnTextSpan) {
          nextBtnTextSpan.textContent = originalBtnText;
        }
      } finally {
        this._isValidatingZip = false;
        // Re-enable ALL buttons that might have been disabled
        if (btnToDisable) {
          btnToDisable.disabled = false;
        }
        if (footerBtn && footerBtn !== btnToDisable) {
          footerBtn.disabled = false;
        }
        if (submitBtn && submitBtn !== btnToDisable) {
          submitBtn.disabled = false;
        }
      }
    });
  }

  populateServicesOptions(showInstallationOptions = false) {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;
    contentWrapper.innerHTML = '';
    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content';
    panelContent.dataset.tabContent = 'services';
    const installationOptions = this.modelConfig?.services?.installation?.options || [];

    // Show installation options if:
    // 1. Explicitly requested (after form submission), OR
    // 2. User already has valid form data and price data (returning to Services tab)
    const hasValidData = this.hasValidUserFormData() && this.finalPriceData;
    if (showInstallationOptions || hasValidData) {
      if (this.hasValidUserFormData()) {
        this.showInstallationOptionsContent(contentWrapper, panelContent, installationOptions);
        return;
      }
    }

    this.userFormData = null;
    window.studioShedUserData = null;
    this.finalPriceData = null;
    this._finalPriceDataZip = null;

    panelContent.innerHTML = this.getSaveDesignFormHTML();
    contentWrapper.appendChild(panelContent);
    this.attachSaveDesignFormHandlers(panelContent);
    this.updateTotalPrice(true);
    this.updateNavButtons();
    const nextBtnText = $('[data-next-step-text]', this.step2Section);
    if (nextBtnText) nextBtnText.textContent = 'SAVE DESIGN';
    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) headerSaveBtn.style.display = 'none';
  }

  showInstallationOptionsContent(contentWrapper, panelContent, installationOptions) {
    installationOptions = installationOptions.reverse();
    const currentValue = this.currentConfig.installation || installationOptions.find(o => o.isDefault)?.value || installationOptions[0]?.value;
    if (installationOptions.length && currentValue) this.currentConfig.installation = currentValue;

    if (!installationOptions.length) {
      panelContent.innerHTML = `<div class="config-section services-section" data-config-section="installation"><p class="services-header fw-medium mb-1" data-services-header>Installation</p><p class="m-0 mt-2 text-gray-600" data-services-empty>No installation options configured.</p></div>`;
      contentWrapper.appendChild(panelContent);
      this.updateNavButtons();
      return;
    }

    panelContent.innerHTML = `<div class="config-section services-section" data-config-section="installation"><p class="services-header fw-medium mb-1" data-services-header>Installation</p><div class="services-options" data-services-options>${installationOptions.map(option => this.renderServiceOption(option, option.value === currentValue)).join('')}</div></div>`;
    contentWrapper.appendChild(panelContent);

    qa('[data-service-option]', '.service-option-card', panelContent).forEach(card => {
      card.addEventListener('click', () => {
        const { section, value } = card.dataset;
        qa('[data-service-option]', '.service-option-card', card.closest('[data-services-options], .services-options')).forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        this.currentConfig[section] = value;
        this.updateTotalPrice(true);
        if (typeof this.recalculateSummaryTotal === 'function') this.recalculateSummaryTotal();
      });
    });

    const headerSaveBtn = $('[data-save-design]', this.step2Section);
    if (headerSaveBtn) { headerSaveBtn.style.display = ''; headerSaveBtn.textContent = 'START OVER'; headerSaveBtn.dataset.startOver = 'true'; }
    this.updateTotalPrice(true);
    this.updateNavButtons();
    const installation = this.finalPriceData?.breakdown?.installation;
    if (installation) {
      window.ConfiguratorShared.applyInstallationCostsToServiceOptions({ container: panelContent, installation, formatPrice: (val) => window.ConfiguratorShared.formatPriceSmart(val, { noDecimals: true }), prefixPlus: true });
    }
  }

  renderServiceOption(option, isSelected) {
    const priceDisplay = option.price > 0 ? `<span class="service-price" data-service-price style="letter-spacing: -0.02em;">${formatPrice(option.price)}</span>` : '';
    const noteHTML = option.note ? `<p class="fs-sm fw-medium mt-4 mb-0" data-service-note>${option.note}</p>` : '';
    return `
      <div class="service-option-card${isSelected ? ' selected' : ''}" data-service-option data-section="installation" data-value="${option.value}" data-display-name="${option.displayName}" data-price="${option.price || 0}">
        <div class="service-option-header d-flex align-items-center justify-content-between mb-2" data-service-header>
          <p class="service-option-title fw-medium mb-0" data-service-title style="letter-spacing: -0.02em;">${option.displayName}</p>
          ${priceDisplay}
          <span class="aspect-variant-check" data-service-checkmark>
            <svg viewBox="0 0 8 7" fill="none" style="width: 11px; height: 6px; margin-bottom: -1px;">
              <path d="M0.5 3.35714L2.77027 5.5L7.5 0.5" stroke="currentColor" stroke-linecap="round"></path>
            </svg>
          </span>
        </div>
        <p class="fs-sm mb-0" data-service-description style="letter-spacing: -0.01em;">${option.description}</p>
        ${noteHTML}
      </div>
    `;
  }

  // ============================================================================
  // SUMMARY
  // ============================================================================

  showSummary() {
    // Summary tab should be disabled when form is invalid.
    // Do NOT redirect - just prevent display if somehow accessed.
    if (!this.hasValidUserFormData()) {
      // Ensure Summary tab is disabled
      this.updateSummaryTabState();
      return;
    }

    this.showThankYouSummary();

    // Hide panel footer on summary page
    const panelFooter = q('[data-panel-footer]', '.panel-footer', this.step2Section);
    if (panelFooter) panelFooter.style.display = 'none';

    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'START OVER';
      saveDesignBtn.dataset.startOver = 'true';
    }
  }

  showThankYouSummary() {
    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    // Change layout to 6-6 columns on thank you page (matching Aspect design)
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-8');
      viewer.classList.add('col-lg-6');
      // Center the image viewer content for col-6 layout
      const imageContainer = $('[data-image-viewer], .image-viewer-container', viewer);
      if (imageContainer) {
        imageContainer.style.setProperty('max-width', '100%', 'important');
        imageContainer.style.setProperty('margin', '0 auto', 'important');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-4');
      panel.classList.add('col-lg-6');
    }

    // Add product-summary-tab class to data-configurator-main element
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.add('product-summary-tab');
    }

    contentWrapper.innerHTML = '';

    const panelContent = document.createElement('div');
    panelContent.className = 'panel-content summary-panel';
    panelContent.dataset.tabContent = 'summary';
    panelContent.innerHTML = this.buildThankYouHTML();
    contentWrapper.appendChild(panelContent);

    this.setupThankYouEventListeners(panelContent);
    this.recalculateSummaryTotal();
  }

  buildThankYouHTML() {
    const variantTitle = this.step1Section?.querySelector('h1')?.textContent || '';
    const productPrice = this.basePrice + this.configPrice;
    
    const installationOptions = this.modelConfig?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    
    const installationName = selectedInstallation?.displayName || '';
    
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.modelConfig?.shippingPrice || 0);
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;
    const zipCode = this.userFormData?.zipCode || '';
    const configSummary = this.buildConfigSummaryString();

    // Build product title
    const productTitle = variantTitle;

    // Build shipping label with city and state_code from API
    const city = this.finalPriceData?.city || '';
    const stateCode = this.finalPriceData?.state_code || '';
    const shippingLocation = this.modelConfig?.shippingLocation || '';
    
    let shippingLabel = 'Shipping';
    if (city && stateCode && zipCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode} (${zipCode})`;
    } else if (city && stateCode) {
      shippingLabel = `Shipping to ${city}, ${stateCode}`;
    } else if (shippingLocation && zipCode) {
      shippingLabel = `Shipping to ${shippingLocation} (${zipCode})`;
    } else if (zipCode) {
      shippingLabel = `Shipping (${zipCode})`;
    }

    return `
      <div class="summary-content summary-thankyou">
        <div class="thank-you-header-box mb-md-6 mb-4">
          <h2 class="h3 mb-3 thank-you-title">Thank you!</h2>
          <p class="thank-you-description mb-0" style="letter-spacing: -0.02em;">Your order summary has been emailed, and a dedicated team member will contact you shortly to guide you through the next steps and help you understand the entire process, ensuring everything is handled seamlessly.</p>
        </div>
        
        <div class="summary-product-section mb-2">
          <div class="d-flex align-items-end justify-content-between mb-2">
            <h3 class="h4 mb-0 summary-product-title">${productTitle}</h3>
            <span class="fw-medium" style="line-height: 1.4; letter-spacing: -0.02em;">${formatPrice(productPrice)}</span>
          </div>
          ${configSummary ? `
          <div class="d-flex align-items-center justify-content-between">
            <p class="fs-sm text-gray-600 mb-0" style="letter-spacing: -0.01em;">${configSummary}</p>
            <a href="#" class="text-decoration-underline" data-edit-design>Edit Design</a>
          </div>
          ` : ''}
        </div>
        
        <div class="summary-price-details-box mb-md-4 mb-3" data-price-details-box>
          <button class="price-details-toggle d-flex align-items-center justify-content-between" data-toggle-price-details>
            <span>Price details</span>
            <span class="d-flex align-items-center" data-toggle-icon>
              <svg aria-hidden="true" focusable="false" style="width:15px; height:15px;">
                <use href="#icon-modal-plus"></use>
              </svg>
            </span>
          </button>
          <div data-price-details-content>
            <div class="price-details-content">${this.buildPriceDetailsHTML()}</div>
          </div>
        </div>
        
        <div class="summary-line-items" data-summary-line-items>
          <div class="summary-line-item d-flex align-items-center justify-content-between gap-2 py-md-4 py-3" data-summary-line-item="shipping">
            <span data-line-item-label>${shippingLabel}</span>
            <span class="fw-medium" data-line-item-price>${shippingPrice > 0 ? formatPrice(shippingPrice) : 'Included'}</span>
          </div>
          ${installationPrice > 0 && installationName ? `
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="installation">
            <span class="line-item-label" data-line-item-label>${installationName} <span class="line-item-note" data-line-item-note>(subject to availability)</span></span>
            <span class="line-item-price" data-line-item-price>${formatPrice(installationPrice)}</span>
          </div>` : ''}
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="permit">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span data-line-item-label>${this.permitPlanSelected === 'include' ? 'Include Permit Plans' : 'No Permit Plans'}</span>
              <span class="fw-medium" data-permit-price-display>${this.permitPlanPrice > 0 ? formatPrice(this.permitPlanPrice) : 'No charge'}</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-permit-plans>Change permit plans</button>
          </div>
          <div class="summary-line-item py-md-4 py-3" data-summary-line-item="foundation">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2" data-line-item-main>
              <span class="line-item-label" data-foundation-label>Foundation TBD</span>
              <span class="fw-medium" data-line-item-price>Cost not included</span>
            </div>
            <button class="bg-transparent border-0 fw-light text-decoration-underline p-0" style="letter-spacing: -0.02em;" data-change-foundation-type>Change foundation type</button>
          </div>
        </div>
        
        <div class="pt-lg-4 pb-lg-5 py-3" data-summary-total>
          <div class="d-flex justify-content-between mb-2" data-summary-total-row>
            <h5 class="mb-0" data-total-label>Estimated Total</h5>
            <h5 class="mb-0" data-summary-total-price>${formatPrice(estimatedTotal)}</h5>
          </div>
          <p class="fs-sm mb-0" data-total-note style="letter-spacing: -0.01em;">${this.getTotalNoteText()}</p>
        </div>

        ${this.buildCTASectionHTML()}
        ${this.buildDesignDetailsHTML(variantTitle)}
      </div>
    `;
  }

  buildConfigSummaryString() {
    const parts = [];

    // Include all selected option display strings (excluding service/summary-only keys)
    Object.entries(this.currentConfig || {}).forEach(([key, value]) => {
      if (!value) return;
      if (key === 'installation') return; // shown separately
      if (key === 'basePrice' || key === 'configPrice' || key === 'totalPrice') return;
      parts.push(formatOptionValue(String(value)));
    });

    return parts.filter(Boolean).join(' | ');
  }

  buildPriceDetailsHTML() {
    const variantTitle = this.step1Section?.querySelector('h1')?.textContent || '';
    
    let html = '';
    
    // Only show main section if we have a label
    if (variantTitle) {
      html += `
        <div class="price-detail-item price-detail-main">
          <div class="price-detail-info">
            <span class="d-inline-block mb-2">${variantTitle}</span>
          </div>
          <span>${formatPrice(this.basePrice)}</span>
        </div>
      `;
    }

    // Add price details for selected options that have prices
    Object.entries(this.currentConfig || {}).forEach(([key, value]) => {
      if (!value || key === 'installation') return;
      
      // Find the option to get its price
      const section = $(`[data-config-section="${key}"]`, this.step2Section);
      if (section) {
        const selectedCard = section.querySelector('.option-card.selected');
        if (selectedCard) {
          const price = normalizeNumber(selectedCard.dataset.price, 0);
          if (price > 0) {
            html += `
              <div class="price-detail-item">
                <span class="price-detail-label">${formatOptionValue(key)}: ${formatOptionValue(String(value))}</span>
                <span class="price-detail-price">${formatPrice(price)}</span>
              </div>
            `;
          }
        }
      }
    });

    return html;
  }

  buildCTASectionHTML() {
    // Get CTA config from modelConfig or use Carriage-specific defaults
    const productName = this.step1Section?.querySelector('h1')?.textContent || 'Carriage House';
    
    // Carriage-specific default configuration (matching Aspect design)
    const defaultCta = {
      calculator: {
        title: 'Need A Quick Estimate?',
        link: '/pages/project-cost-calculator',
        linkText: 'TRY PROJECT COST CALCULATOR'
      },
      financing: {
        title: 'Monthly Financing Available!',
        subtitle: 'Plan smarter with the right financing',
        link: '/pages/financing',
        linkText: 'EXPLORE FINANCING OPTIONS'
      },
      consultation: {
        title: 'Schedule a FREE Online Consultation',
        description: 'Select the available time that works for you to explore your project goals, budget, build site, and initial design.',
        link: '/pages/consultation',
        buttonText: 'BOOK A FREE CONSULTATION'
      }
    };
    
    // PDF icon SVG for downloads
    const pdfIcon = `<svg viewBox="0 0 24 24" fill="none" style="width: 24px; height: 24px;">
      <g clip-path="url(#clip0_1618_10352)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.67107 22.95C-1.00893 20.91 0.77757 18.48 2.60607 17.445C3.62157 16.8735 4.88607 16.476 6.25107 16.101C5.84487 16.8749 5.3967 17.6261 4.90857 18.351C4.50698 18.5122 4.11573 18.6981 3.73707 18.9075C2.77407 19.4505 1.48707 20.4 2.27757 21.5925C3.04707 22.7535 4.28757 21.477 4.87257 20.8035C5.61957 20.0415 6.46257 18.8385 7.21257 17.5185C8.00607 16.1235 8.72757 14.5485 9.41757 12.8835C10.0401 11.3835 10.5936 9.83855 11.0076 8.39855C11.4276 6.95855 11.7111 5.62505 11.8161 4.50005C11.9016 3.56555 11.8611 2.19005 10.6311 2.37005C9.40107 2.55005 9.41607 3.99005 9.70857 5.11505C9.79457 5.45005 9.90257 5.79755 10.0326 6.15755C9.94057 6.58855 9.82907 7.04355 9.69807 7.52255C9.58807 7.91855 9.46807 8.32405 9.33807 8.73905C8.80876 7.706 8.38995 6.61999 8.08857 5.49905C7.56357 3.47405 7.54107 0.654046 10.2636 0.0690461C12.9861 -0.515954 13.6686 2.57405 13.4886 4.55405C13.3386 6.15905 12.8931 8.12405 12.2181 10.149C12.6501 10.8645 13.1211 11.5815 13.6206 12.279C14.1456 13.0155 14.8356 13.7685 15.4056 14.424C17.4156 14.274 19.3956 14.373 20.8806 14.6535C22.9206 15.039 24.7656 16.3185 23.6856 18.7635C22.6041 21.2085 19.9356 20.8485 18.1656 19.71C16.6893 18.7558 15.3942 17.5471 14.3406 16.14H14.3361C13.2173 14.4622 12.1907 12.7246 11.2611 10.935C10.8171 12.2745 10.1661 14.07 9.61107 15.27C10.3206 15.105 11.0661 14.97 11.8011 14.8575C12.1026 15.3645 12.3906 15.8595 12.6861 16.3395C12.4511 16.3745 12.2061 16.4135 11.9511 16.4565C11.0281 16.6135 10.1231 16.8015 9.23607 17.0205C9.01207 17.4505 8.79507 17.8745 8.58507 18.2925C7.83657 19.7745 7.14657 21.1425 6.29007 22.1175C4.92057 23.6775 2.36007 24.9525 0.68007 22.9125L0.67107 22.95ZM16.4211 15.93C17.3196 16.848 18.1611 17.55 18.9861 18.09C19.9881 18.735 21.1461 19.1115 21.6861 17.985C22.2366 16.8555 21.2196 16.29 20.1561 16.08C19.1076 15.8805 17.8611 15.8715 16.4061 15.924L16.4211 15.93Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_1618_10352">
          <rect width="24" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
    
    // Build downloads from variant metafield documents
    const variantDocs = (this.variantDocuments || []).map(doc => ({
      icon: pdfIcon,
      title: doc.alt || doc.filename || `${productName} Product Guide`,
      url: doc.url
    }));
    
    const ctaConfig = this.modelConfig?.cta || defaultCta;
    const downloads = variantDocs.length ? variantDocs : (this.modelConfig?.downloads || []);

    let html = '';

    // CTA Cards section
    const calculator = ctaConfig.calculator || defaultCta.calculator;
    const financing = ctaConfig.financing || defaultCta.financing;
    
    html += `<div class="d-flex flex-column gap-2 pb-5" data-summary-cta-section>`;

       // Consultation section
       const consultation = ctaConfig.consultation || defaultCta.consultation;
       html += `
         <div class="py-4 border-top border-gray-200" data-summary-consultation>
           <h5 class="mb-2 d-none" data-consultation-title>${consultation.title}</h5>
           <p class="mb-lg-5 mb-3 pe-xl-6" style="letter-spacing: -0.02em;" data-consultation-description>${consultation.description}</p>
           <a href="${consultation.link}" class="btn btn-primary w-100" data-consultation-btn target="_blank">${consultation.buttonText}</a>
         </div>
       `;
    
    // Calculator card
    html += `
      <div class="calculator-card position-relative overflow-hidden d-flex flex-wrap align-items-center justify-content-between bg-primary-100 rounded gap-2" data-cta-card="calculator">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="calculator-icon position-absolute">
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/calculator.svg?v=1767594243" alt="Calculator" title="Calculator" class="w-100 h-100"/>
          </div>
          <span class="fw-medium" data-cta-text>${calculator.title}</span>
        </div>
        <a href="${calculator.link}" class="d-flex align-items-center gap-2 text-decoration-none fw-medium" data-cta-link target="_blank">
          ${calculator.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

  

    // Financing card
    html += `
      <div class="financing-card d-flex flex-wrap align-items-center justify-content-between bg-secondary rounded gap-2 d-none" data-cta-card="financing">
        <div class="d-flex align-items-center gap-lg-6 gap-3 me-3" data-cta-content>
          <div class="d-flex flex-column gap-2" data-financing-logos>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/acorn.png?v=1767596474" class="acorn-logo" alt="Acorn Finance" title="Acorn Finance"/>
            <img src="https://cdn.shopify.com/s/files/1/0969/5304/1190/files/renofi.png?v=1767596473" class="renofi-logo" alt="RenoFi" title="RenoFi"/>
          </div>
          <div class="d-flex flex-column" data-financing-text>
            <p class="fw-medium mb-1">${financing.title}</p>
            <span class="fs-sm" style="letter-spacing: -0.01em;">${financing.subtitle}</span>
          </div>
        </div>
        <a href="${financing.link}" class="d-flex align-items-center gap-2 text-decoration-none text-black fw-medium" data-cta-link target="_blank">
          ${financing.linkText}
          <svg viewBox="0 0 11 11" fill="none" style="width: 11px; height: 11px;">
            <path d="M5.5 0.5L10.5 5.5M10.5 5.5L5.5 10.5M10.5 5.5L0.5 5.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    `;

    html += `</div>`;

    // Downloads section
    if (downloads.length) {
      html += `
        <div class="py-4 border-top border-gray-200" data-summary-downloads>
          <h5 class="mb-3" data-downloads-title>Downloads</h5>
          ${downloads.map(dl => `
            <a href="${dl.url || '#'}" class="d-flex align-items-center gap-3 text-decoration-none text-black" data-download-link target="_blank">
              <span data-download-icon>${dl.icon}</span>
              <span class="text-decoration-underline">${dl.title || ''}</span>
            </a>
          `).join('')}
        </div>
      `;
    }

    return html;
  }

  buildDesignDetailsHTML(variantTitle) {
    const config = this.currentConfig || {};
    const modelData = this.modelConfig || {};

    // Helper to render detail row only if value exists
    const renderDetailRow = (label, value) => {
      if (!value) return '';
      return `
        <div class="design-detail-col">
          <h6 class="mb-3">${label}</h6>
          <p class="mb-0">${formatOptionValue(String(value))}</p>
        </div>
      `;
    };

    // Build model specs line
    const specParts = [];
    if (modelData.depth) specParts.push(`Depth: ${modelData.depth}`);
    if (modelData.length) specParts.push(`Length: ${modelData.length}`);
    if (modelData.squareFootage) specParts.push(`${modelData.squareFootage} sq. ft.`);
    const specsLine = specParts.length ? `<p>${specParts.join(' | ')}</p>` : '';

    // Build detail options rows from config
    const detailRows = [];
    Object.entries(config || {}).forEach(([key, value]) => {
      if (!value || key === 'installation') return;
      const label = formatOptionValue(key).toUpperCase();
      detailRows.push({ label, value });
    });

    let html = `<div class="summary-design-details"><h5 class="mb-md-5 mb-3">Design Details</h5>`;

    // MODEL section
    html += `
      <div class="design-details-grid">
        <div class="design-detail-col">
          <h6 class="mb-md-3 mb-2">MODEL</h6>
          ${variantTitle ? `<p class="mb-2">${variantTitle}</p>` : ''}
          ${specsLine}
        </div>
      </div>
    `;

    // Detail options rows (in pairs)
    if (detailRows.length > 0) {
      for (let i = 0; i < detailRows.length; i += 2) {
        html += `<div class="design-details-grid design-details-options">`;
        html += renderDetailRow(detailRows[i].label, detailRows[i].value);
        if (detailRows[i + 1]) {
          html += renderDetailRow(detailRows[i + 1].label, detailRows[i + 1].value);
        }
        html += `</div>`;
      }
    }

    // Get includes/excludes from modelConfig or use defaults
    const includes = this.modelConfig?.includes || [
      'Exterior Shell with Lap Siding',
      'Interior Framing',
      'Interior Insulation and flooring',
      'Electrical, lighting, appliance package',
      'Complete kitchen and/or bathroom package',
      'Door and window hardware'
    ];
    const excludes = this.modelConfig?.excludes || [
      'Foundation and site utilities',
      'Drywall (included with ProAssembly service)',
      'Permit fees',
      'Sales tax as applicable'
    ];

    // Includes/Excludes section (only if data exists)
    if (includes.length || excludes.length) {
      html += `
        <div class="summary-section-divider"></div>
        <div class="summary-includes-excludes">
          <div class="includes-excludes-grid">
            ${includes.length ? `
            <div class="includes-col mb-sm-0 mb-5">
              <h6 class="mb-3">INCLUDES</h6>
              <ul class="ps-xl-5 mb-0">${includes.map(item => `<li>${item}</li>`).join('')}</ul>
            </div>
            ` : ''}
            ${excludes.length ? `
            <div class="excludes-col">
              <h6 class="mb-3">EXCLUDES</h6>
              <ul class="ps-xl-5 mb-0">${excludes.map(item => `<li>${item}</li>`).join('')}</ul>
            </div>
            ` : ''}
          </div>
        </div>
      `;
    }

    html += `</div>`;
    return html;
  }

  setupThankYouEventListeners(panel) {
    const editLink = $('[data-edit-design]', panel);
    if (editLink) {
      editLink.addEventListener('click', (e) => {
        e.preventDefault();
        const defaultTab = this.getAvailableTabs()[0]?.name || 'services';
        this.switchTab(defaultTab);
        const footer = $('[data-panel-footer]', this.step2Section);
        if (footer) footer.style.display = '';
      });
    }

    const priceToggle = $('[data-toggle-price-details]', panel);
    const priceContent = $('[data-price-details-content]', panel);
    if (priceToggle && priceContent) {
      // Setup smooth dropdown animation styles
      priceContent.style.overflow = 'hidden';
      priceContent.style.transition = 'max-height 0.35s ease-in-out, opacity 0.3s ease-in-out';
      priceContent.style.maxHeight = '0';
      priceContent.style.opacity = '0';
      priceContent.style.display = 'block'; // Always block, control visibility via max-height
      
      priceToggle.addEventListener('click', () => {
        const isCollapsed = priceContent.style.maxHeight === '0px' || priceContent.style.maxHeight === '0';
        
        if (isCollapsed) {
          // Expand: measure content height and animate to it
          priceContent.style.maxHeight = priceContent.scrollHeight + 'px';
          priceContent.style.opacity = '1';
        } else {
          // Collapse: animate to 0
          priceContent.style.maxHeight = '0';
          priceContent.style.opacity = '0';
        }
        
        const icon = priceToggle.querySelector('[data-toggle-icon], .toggle-icon');
        if (icon) {
          // Handle SVG icon
          const svg = icon.querySelector('svg');
          if (svg) {
            icon.innerHTML = isCollapsed 
              ? `<svg aria-hidden="true" focusable="false" style="width:15px; height:15px;"><use href="#icon-modal-minus"></use></svg>` 
              : `<svg aria-hidden="true" focusable="false" style="width:15px; height:15px;"><use href="#icon-modal-plus"></use></svg>`;
          } else {
            icon.textContent = isCollapsed ? '−' : '+';
          }
        }
      });
    }

    const changePermit = $('[data-change-permit-plans]', panel);
    if (changePermit) changePermit.addEventListener('click', (e) => { e.preventDefault(); this.openPermitPlansDrawer(); });

    const changeFoundation = $('[data-change-foundation-type]', panel);
    if (changeFoundation) changeFoundation.addEventListener('click', (e) => { e.preventDefault(); this.openFoundationTypeDrawer(); });
  }

  // ============================================================================
  // PERMIT PLANS DRAWER - Using shared PermitManager
  // ============================================================================

  setupPermitPlansDrawer() {
    this.permitManager = new window.ConfiguratorShared.PermitManager({
      configurator: this,
      drawerId: 'permit-plans-drawer',
      basePrice: this.modelConfig?.permitPlanPrice || 6995,
      formatPrice: formatPrice,
      onSelect: () => this.recalculateSummaryTotal(),
      step2Section: this.step2Section
    });
    this.permitManager.setup();
  }

  openPermitPlansDrawer() {
    this.permitManager?.open();
  }

  updateSummaryPermitDisplay() {
    this.permitManager?.updateSummaryDisplay();
  }

  // ============================================================================
  // FOUNDATION TYPE DRAWER
  // ============================================================================

  setupFoundationTypeDrawer() {
    this.foundationManager = new window.ConfiguratorShared.FoundationManager({
      configurator: this,
      drawerId: 'foundation-type-drawer',
      step2Section: this.step2Section
    });
    this.foundationManager.setup();
  }

  openFoundationTypeDrawer() {
    this.foundationManager?.open();
  }

  updateFoundationDrawerSelection() {
    this.foundationManager?.updateSelection();
  }

  handleFoundationOptionSelect(card) {
    this.foundationManager?.handleSelect(card);
  }

  updateSummaryFoundationDisplay() {
    this.foundationManager?.updateSummaryDisplay();
  }

  recalculateSummaryTotal() {
    const productPrice = this.basePrice + this.configPrice;
    const shippingPrice = normalizeNumber(this.finalPriceData?.shipping_cost, this.modelConfig?.shippingPrice || 0);
    const installationOptions = this.modelConfig?.services?.installation?.options || [];
    const selectedInstallation = installationOptions.find(opt => opt.value === this.currentConfig.installation);
    
    // Get installation price from API breakdown
    // For "shell-lifestyle" option, use lifestyle price from API breakdown
    let installationPrice = 0;
    const installationValue = this.currentConfig.installation || '';
    
    if (this.finalPriceData?.breakdown?.installation) {
      const breakdown = this.finalPriceData.breakdown.installation;
      
      // If user selected "shell-lifestyle", use lifestyle price from API breakdown
      if (installationValue.toLowerCase().includes('lifestyle')) {
        installationPrice = normalizeNumber(breakdown.lifestyle, 0);
      } else {
        // For other options, use the computed installation cost
        const computedCents = window.ConfiguratorShared.computeInstallationCost(
          breakdown,
          installationValue
        );
        installationPrice = computedCents > 0 ? computedCents : normalizeNumber(selectedInstallation?.price, 0);
      }
    } else {
      // Fallback if no API data available
      installationPrice = normalizeNumber(selectedInstallation?.price, 0);
    }
    const estimatedTotal = productPrice + shippingPrice + this.permitPlanPrice + installationPrice;

    const contentWrapper = $('[data-panel-content-wrapper]', this.step2Section);
    if (!contentWrapper) return;

    const totalDisplay = $('[data-summary-total-price]', contentWrapper);
    if (totalDisplay) totalDisplay.textContent = formatPrice(estimatedTotal);

    const totalNote = q('[data-total-note]', '.total-note', contentWrapper);
    if (totalNote) {
      totalNote.innerHTML = this.getTotalNoteText();
    }

    // Update ALL price elements (header and mobile footer)
    const headerPrices = $$('[data-configurator-price]', this.step2Section);
    headerPrices.forEach(el => el.textContent = formatPrice(estimatedTotal));
  }

  handleStartOver() {
    // Preserve roofStyle from variant selection (captured in showStep2)
    const preservedRoofStyle = this.currentConfig.roofStyle;
    
    this.currentConfig = {};
    this.configPrice = 0;
    this.userFormData = null;
    this.currentImages = {};
    this.permitManager?.reset();
    this.foundationManager?.reset();

    // Restore roofStyle after reset
    if (preservedRoofStyle) {
      this.currentConfig.roofStyle = preservedRoofStyle;
    }

    this.initializeDefaultServices();

    // Reset layout from col-6 back to col-8/col-4
    this.restoreDefaultLayout();

    // Remove product-summary-tab class
    const configuratorMain = $('[data-configurator-main]', this.step2Section);
    if (configuratorMain) {
      configuratorMain.classList.remove('product-summary-tab');
    }

    const saveDesignBtn = $('[data-save-design]', this.step2Section);
    if (saveDesignBtn) {
      saveDesignBtn.textContent = 'SAVE DESIGN';
      delete saveDesignBtn.dataset.startOver;
    }

    const footer = $('[data-panel-footer]', this.step2Section);
    if (footer) footer.style.display = '';

    const defaultTab = this.getAvailableTabs()[0]?.name || 'services';
    this.switchTab(defaultTab);
  }

  /**
   * Restore default 8-4 column layout (from 6-6 thank you page layout)
   */
  restoreDefaultLayout() {
    const viewer = $('.configurator-viewer', this.step2Section);
    const panel = $('.configurator-panel', this.step2Section);
    if (viewer) {
      viewer.classList.remove('col-lg-6');
      viewer.classList.add('col-lg-8');
      const imageContainer = $('[data-image-viewer], .image-viewer-container', viewer);
      if (imageContainer) {
        imageContainer.style.removeProperty('max-width');
        imageContainer.style.removeProperty('margin');
      }
    }
    if (panel) {
      panel.classList.remove('col-lg-6');
      panel.classList.add('col-lg-4');
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Generate the total note text based on installation selection
   * Installation options:
   * - "shell" or "product-only" = Product Only (no assembly)
   * - "lifestyle" = ProAssembly: Shell Only (has assembly)
   * - "kitchenbath" = ProAssembly: Shell + Lifestyle Interior (has assembly)
   * @returns {string} HTML string for the total note
   */
  getTotalNoteText() {
    const installation = (this.currentConfig?.installation || '').toLowerCase();
    // "shell" and "product-only" mean no assembly, anything else means assembly is included
    const noAssemblyValues = ['shell', 'product-only', 'product_only', 'productonly', ''];
    const hasAssembly = !noAssemblyValues.includes(installation);
    const hasPermitPlans = this.permitPlanSelected === 'include';
    
    let includes = ['product', 'delivery'];
    if (hasAssembly) includes.push('assembly');
    if (hasPermitPlans) includes.push('permit plans');
    
    // Format the includes list with proper grammar
    let includesText;
    if (includes.length === 2) {
      includesText = includes.join(' and ');
    } else {
      const last = includes.pop();
      includesText = includes.join(', ') + ', and ' + last;
    }
    
    return `Includes ${includesText}.<br>Excludes foundation, site utilities, HVAC, decks, upgrades, permitting, local taxes.`;
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  dispatchVariantChange(variantId, price) {
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variantId, price } }));
  }

  getSelectedVariantId() {
    const selectedCard = this.step1Section.querySelector('[data-variant-card].selected, .carriage-variant-card.selected');
    return selectedCard?.dataset.variantId || null;
  }

  getConfiguration() {
    return {
      ...this.currentConfig,
      basePrice: this.basePrice,
      configPrice: this.configPrice,
      totalPrice: this.basePrice + this.configPrice,
      permitPlan: this.permitPlanSelected,
      permitPlanPrice: this.permitPlanPrice,
      foundationType: this.foundationTypeSelected,
      userFormData: this.userFormData
    };
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initCarriageConfigurator() {
  if (!window.carriageConfigurator) {
    window.carriageConfigurator = new CarriageConfigurator();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarriageConfigurator);
} else {
  initCarriageConfigurator();
}

